// How the connection to /api is doing, for /app on a weak network (F203).
// Browser state, shared by every read (server code loads this through
// api-client but never sends a request, so nothing here runs there):
// - slow: a read has been waiting SLOW_AFTER_MS or is being retried;
// - failed: a read gave up after its retries; its skeletons say so (Loading)
//   until retry() reloads the page's data;
// - generation: bumped by retry(); DashboardShell keys /app's page on it, so
//   every page's loads run again.
// retry() also runs when the phone comes back online or the app is brought
// back to the front while something has failed.

export const SLOW_AFTER_MS = 5_000;

export interface ConnectionState {
  slow: boolean;
  failed: boolean;
  generation: number;
}

let state: ConnectionState = { slow: false, failed: false, generation: 0 };
let slowReads = 0;
const listeners = new Set<() => void>();

function set(next: Partial<ConnectionState>) {
  state = { ...state, ...next };
  for (const l of listeners) l();
}

// No React here: server code imports api-client, which imports this; the hook
// is in use-connection.ts.
export function subscribeConnection(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export const connectionState = () => state;

/** Load everything again: clears `failed`, bumps the generation. */
export function retryConnection() {
  set({ failed: false, generation: state.generation + 1 });
}

/** Clears a failure left by an earlier page, without reloading anything. */
export function clearConnectionFailure() {
  if (state.failed) set({ failed: false });
}

/**
 * Wraps one read: marks the connection slow while it takes longer than
 * SLOW_AFTER_MS (or is retried), and failed if it ends without an answer.
 */
export async function trackRead<T extends { status: number }>(read: (onRetry: () => void) => Promise<T>, gaveUp: (result: T) => boolean): Promise<T> {
  let counted = false;
  const markSlow = () => {
    if (counted) return;
    counted = true;
    slowReads++;
    if (!state.slow) set({ slow: true });
  };
  const timer = setTimeout(markSlow, SLOW_AFTER_MS);
  try {
    const result = await read(markSlow);
    if (gaveUp(result)) set({ failed: true });
    return result;
  } finally {
    clearTimeout(timer);
    if (counted && --slowReads === 0) set({ slow: false });
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    if (state.failed) retryConnection();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && state.failed && navigator.onLine) retryConnection();
  });
}
